import { totauxChargesCri } from '../../lib/criCalculs';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, Plus, Trash2, Users } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { DEFAVORABLE, NEUTRE_DOUX } from '../../lib/couleurs';
import {
  CATEGORIES_INDUSTRIELLES,
  LIGNES_PRODUCTION,
  TAUX_CHARGES_PATRONALES_DEFAUT,
  enregistrerCharge,
  fetchDepensesIndustrialisables,
  importerDepensesIndustrielles,
  importerMasseSalariale,
  supprimerCharge,
} from '../../lib/cri';
import { Carte, btn, btnLight, inputCls, labelCls, td, th } from './ui';

export const ChargesCri = ({ charges, campagne, utilisateur, onMaj }) => {
  const [dispo, setDispo] = useState([]);
  const [sel, setSel] = useState([]);
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const [imp, setImp] = useState({ ligne: LIGNES_PRODUCTION[0], categorie: 'Électricité', periodeDebut: '', periodeFin: '', exceptionnel: false });
  const [paie, setPaie] = useState({ ligne: LIGNES_PRODUCTION[0], periodeDebut: '', periodeFin: '', taux: String(TAUX_CHARGES_PATRONALES_DEFAUT) });
  const [man, setMan] = useState({
    ligne: LIGNES_PRODUCTION[0],
    categorie: 'Maintenance',
    libelle: '',
    ht: '',
    montantTVA: '',
    autresTaxes: '',
    timbreFiscal: '',
    periodeDebut: aujourdhui,
    periodeFin: aujourdhui,
    datePaiement: '',
    exceptionnel: false,
  });

  useEffect(() => {
    fetchDepensesIndustrialisables().then(setDispo);
  }, [charges]);

  const totaux = useMemo(() => totauxChargesCri(charges), [charges]);

  const importer = async () => {
    const { error, ajoutes, ignores, total } = await importerDepensesIndustrielles({
      ids: sel,
      campagne,
      ligne: imp.ligne,
      categorie: imp.categorie,
      periodeDebut: imp.periodeDebut,
      periodeFin: imp.periodeFin,
      exceptionnel: imp.exceptionnel,
      utilisateur,
    });
    if (error) return toast.error(error);
    toast.success(`${ajoutes} facture(s) incorporée(s) pour ${fmt(total, 2)} TND TTC${ignores.length ? ` — ${ignores.length} déjà incorporée(s)` : ''}.`);
    setSel([]);
    onMaj();
  };

  const importerPaie = async () => {
    const { error, brut, total } = await importerMasseSalariale({
      campagne,
      ligne: paie.ligne,
      periodeDebut: paie.periodeDebut,
      periodeFin: paie.periodeFin,
      tauxChargesPatronales: Number(paie.taux) || 0,
      utilisateur,
    });
    if (error) return toast.error(error);
    toast.success(`Masse salariale incorporée : ${fmt(brut, 2)} TND de brut → ${fmt(total, 2)} TND chargés.`);
    onMaj();
  };

  const saisir = async () => {
    const { error } = await enregistrerCharge({ ...man, campagne, utilisateur });
    if (error) return toast.error(error);
    toast.success('Charge industrielle enregistrée.');
    setMan((p) => ({ ...p, libelle: '', ht: '', montantTVA: '', autresTaxes: '', timbreFiscal: '' }));
    onMaj();
  };

  const retirer = async (id) => {
    const { error } = await supprimerCharge(id);
    if (error) return toast.error(error);
    toast.success('Charge retirée du périmètre analytique.');
    onMaj();
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-6">
        {[
          ['Charges normales incorporées', `${fmt(totaux.normales, 2)} TND`],
          ...totaux.parCategorie.map(([c, v]) => [c, `${fmt(v, 2)} TND`]),
          ['Exceptionnelles (hors CRI)', `${fmt(totaux.exceptionnelles, 2)} TND`],
        ].map(([k, v]) => (
          <div key={k} className="bg-white px-3 py-2.5">
            <p className="text-[11px] text-slate-500">{k}</p>
            <p className="tabular font-data text-base font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>

      <Carte
        testid="cri-import-factures"
        titre="Incorporer des factures de frais généraux"
        sous="TVA des charges industrielles non récupérable à 100 % : la facture est incorporée pour son TTC. Rattachement à la période de consommation ou de service, jamais à la date de paiement."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="block">
            <span className={labelCls}>Ligne</span>
            <select data-testid="cri-import-ligne" value={imp.ligne} onChange={(e) => setImp((p) => ({ ...p, ligne: e.target.value }))} className={inputCls}>
              {LIGNES_PRODUCTION.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Catégorie</span>
            <select data-testid="cri-import-categorie" value={imp.categorie} onChange={(e) => setImp((p) => ({ ...p, categorie: e.target.value }))} className={inputCls}>
              {CATEGORIES_INDUSTRIELLES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Période consommée — début</span>
            <input data-testid="cri-import-debut" type="date" value={imp.periodeDebut} onChange={(e) => setImp((p) => ({ ...p, periodeDebut: e.target.value }))} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Période consommée — fin</span>
            <input data-testid="cri-import-fin" type="date" value={imp.periodeFin} onChange={(e) => setImp((p) => ({ ...p, periodeFin: e.target.value }))} className={inputCls} />
          </label>
          <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
            <input data-testid="cri-import-exceptionnel" type="checkbox" checked={imp.exceptionnel} onChange={(e) => setImp((p) => ({ ...p, exceptionnel: e.target.checked }))} />
            Charge exceptionnelle (isolée du CRI)
          </label>
        </div>

        <div className="mt-4 max-h-72 overflow-auto border border-slate-200">
          <table className="w-full min-w-[760px] border-collapse">
            <thead className="sticky top-0 bg-slate-50">
              <tr>
                <th className={th} />
                <th className={th}>Facture</th>
                <th className={th}>Fournisseur</th>
                <th className={th}>Date pièce</th>
                <th className={th}>Catégorie ERP</th>
                <th className={th}>HT</th>
                <th className={th}>TVA</th>
                <th className={th}>TTC incorporable</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {dispo.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={8}>
                    Toutes les factures de frais généraux sont déjà incorporées (ou aucune n’est saisie).
                  </td>
                </tr>
              )}
              {dispo.map((d) => (
                <tr key={d.id} data-testid={`cri-facture-${d.id}`}>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`cri-facture-check-${d.id}`}
                      type="checkbox"
                      checked={sel.includes(d.id)}
                      onChange={(e) => setSel((p) => (e.target.checked ? [...p, d.id] : p.filter((x) => x !== d.id)))}
                    />
                  </td>
                  <td className={td}>{d.numeroFacture}</td>
                  <td className={td}>{d.fournisseur}</td>
                  <td className={td}>{d.date}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{d.categorie}</td>
                  <td className={td}>{fmt(d.montantHT, 2)}</td>
                  <td className={td}>{fmt(d.montantTVA, 2)}</td>
                  <td className={`${td} font-semibold ${DEFAVORABLE}`}>{fmt(d.montantTTC, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button data-testid="cri-import-submit" type="button" onClick={importer} disabled={!sel.length} className={`${btn} mt-4`}>
          <Download size={15} /> Incorporer {sel.length || ''} facture(s)
        </button>
      </Carte>

      <Carte
        testid="cri-import-paie"
        titre="Incorporer la masse salariale pointée"
        sous="Aucune TVA sur les salaires : coût de production = brut pointé + charges patronales. Une période déjà importée ne peut pas être comptée deux fois."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <label className="block">
            <span className={labelCls}>Ligne</span>
            <select data-testid="cri-paie-ligne" value={paie.ligne} onChange={(e) => setPaie((p) => ({ ...p, ligne: e.target.value }))} className={inputCls}>
              {LIGNES_PRODUCTION.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Du</span>
            <input data-testid="cri-paie-debut" type="date" value={paie.periodeDebut} onChange={(e) => setPaie((p) => ({ ...p, periodeDebut: e.target.value }))} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Au</span>
            <input data-testid="cri-paie-fin" type="date" value={paie.periodeFin} onChange={(e) => setPaie((p) => ({ ...p, periodeFin: e.target.value }))} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Charges patronales (%)</span>
            <input data-testid="cri-paie-taux" type="number" step="0.01" value={paie.taux} onChange={(e) => setPaie((p) => ({ ...p, taux: e.target.value }))} className={inputCls} />
          </label>
          <button data-testid="cri-paie-submit" type="button" onClick={importerPaie} className={`${btn} mt-6`}>
            <Users size={15} /> Incorporer la paie
          </button>
        </div>
      </Carte>

      <Carte testid="cri-charge-manuelle" titre="Saisie directe d’une charge industrielle" sous="Le détail HT / TVA / autres taxes est conservé pour expliquer le montant incorporé.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className={labelCls}>Ligne</span>
            <select data-testid="cri-man-ligne" value={man.ligne} onChange={(e) => setMan((p) => ({ ...p, ligne: e.target.value }))} className={inputCls}>
              {LIGNES_PRODUCTION.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Catégorie</span>
            <select data-testid="cri-man-categorie" value={man.categorie} onChange={(e) => setMan((p) => ({ ...p, categorie: e.target.value }))} className={inputCls}>
              {CATEGORIES_INDUSTRIELLES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block lg:col-span-2">
            <span className={labelCls}>Libellé</span>
            <input data-testid="cri-man-libelle" value={man.libelle} onChange={(e) => setMan((p) => ({ ...p, libelle: e.target.value }))} className={inputCls} placeholder="ex. STEG trimestre 4" />
          </label>
          <label className="block">
            <span className={labelCls}>Montant HT</span>
            <input data-testid="cri-man-ht" type="number" step="0.001" value={man.ht} onChange={(e) => setMan((p) => ({ ...p, ht: e.target.value }))} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>TVA (non récupérable)</span>
            <input data-testid="cri-man-tva" type="number" step="0.001" value={man.montantTVA} onChange={(e) => setMan((p) => ({ ...p, montantTVA: e.target.value }))} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Autres taxes</span>
            <input data-testid="cri-man-taxes" type="number" step="0.001" value={man.autresTaxes} onChange={(e) => setMan((p) => ({ ...p, autresTaxes: e.target.value }))} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Timbre fiscal</span>
            <input data-testid="cri-man-timbre" type="number" step="0.001" value={man.timbreFiscal} onChange={(e) => setMan((p) => ({ ...p, timbreFiscal: e.target.value }))} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Période consommée — début</span>
            <input data-testid="cri-man-debut" type="date" value={man.periodeDebut} onChange={(e) => setMan((p) => ({ ...p, periodeDebut: e.target.value }))} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Période consommée — fin</span>
            <input data-testid="cri-man-fin" type="date" value={man.periodeFin} onChange={(e) => setMan((p) => ({ ...p, periodeFin: e.target.value }))} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Date de paiement (indicative)</span>
            <input data-testid="cri-man-paiement" type="date" value={man.datePaiement} onChange={(e) => setMan((p) => ({ ...p, datePaiement: e.target.value }))} className={inputCls} />
          </label>
          <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
            <input data-testid="cri-man-exceptionnel" type="checkbox" checked={man.exceptionnel} onChange={(e) => setMan((p) => ({ ...p, exceptionnel: e.target.checked }))} />
            Charge exceptionnelle
          </label>
        </div>
        <p className="mt-3 font-data text-sm text-slate-700">
          Montant incorporé :{' '}
          <span data-testid="cri-man-apercu" className="font-semibold">
            {fmt((Number(man.ht) || 0) + (Number(man.montantTVA) || 0) + (Number(man.autresTaxes) || 0) + (Number(man.timbreFiscal) || 0), 2)} TND
          </span>
        </p>
        <button data-testid="cri-man-submit" type="button" onClick={saisir} className={`${btn} mt-3`}>
          <Plus size={15} /> Enregistrer la charge
        </button>
      </Carte>

      <Carte testid="cri-charges-liste" titre="Charges industrielles réelles de la campagne">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1040px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                <th className={th}>Pièce</th>
                <th className={th}>Libellé</th>
                <th className={th}>Catégorie</th>
                <th className={th}>Ligne</th>
                <th className={th}>Période consommée</th>
                <th className={th}>Paiement</th>
                <th className={th}>HT</th>
                <th className={th}>TVA non récup.</th>
                <th className={th}>Autres taxes</th>
                <th className={th}>Incorporé</th>
                <th className={th}>Nature</th>
                <th className={th} />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {charges.length === 0 && (
                <tr>
                  <td className={`${td} text-slate-500`} colSpan={12}>
                    Aucune charge industrielle incorporée pour cette campagne.
                  </td>
                </tr>
              )}
              {charges.map((c) => (
                <tr key={c.id} data-testid={`cri-charge-row-${c.id}`}>
                  <td className={td}>{c.pieceNumero || '—'}</td>
                  <td className={td}>{c.libelle}</td>
                  <td className={td}>{c.categorie}</td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{c.ligne}</td>
                  <td className={td}>
                    {c.periodeDebut} → {c.periodeFin}
                  </td>
                  <td className={`${td} ${NEUTRE_DOUX}`}>{c.datePaiement || '—'}</td>
                  <td className={td}>{fmt(c.ht, 2)}</td>
                  <td className={td}>{fmt(c.tvaNonRecuperable, 2)}</td>
                  <td className={td}>{fmt(c.autresTaxes, 2)}</td>
                  <td className={`${td} font-semibold ${DEFAVORABLE}`}>{fmt(c.montantIncorporable, 2)}</td>
                  <td className="px-3 py-2">
                    <span className={`border px-1.5 py-0.5 font-data text-[11px] uppercase ${c.exceptionnel ? 'border-amber-300 bg-amber-50 text-amber-800' : 'border-slate-300 bg-slate-50 text-slate-600'}`}>
                      {c.exceptionnel ? 'Exceptionnelle' : 'Normale'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <button data-testid={`cri-charge-del-${c.id}`} type="button" onClick={() => retirer(c.id)} className={btnLight}>
                      <Trash2 size={13} /> Retirer
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Carte>
    </div>
  );
};
